import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import AddLocationAltOutlinedIcon from "@mui/icons-material/AddLocationAltOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import FilterListIcon from "@mui/icons-material/FilterList";
import MapOutlinedIcon from "@mui/icons-material/MapOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import RefreshIcon from "@mui/icons-material/Refresh";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";
import VisitPlanReports from "../VisitPlanReports/VisitPlanReports";
import "./VisitPlan.css";

const PAGE_SIZE = 5000;
const INITIAL_TABLE_LIMIT = 50;
const TABLE_LIMIT_STEP = 50;
const UNDETERMINED_STATION = "__UNDETERMINED__";
const EMPTY_FILTERS = {
  search: "",
  visitor: "",
  status: "",
  courierStatus: "",
  bank: "",
  city: "",
  excludedCities: [],
  station: "",
  assignment: "all",
  priority: "",
  reviewOrder: "recommended",
};

const apiErrorMessage = (error, fallback) => {
  const data = error?.response?.data;
  if (typeof data?.message === "string" && data.message.trim()) return data.message;
  if (typeof data === "string" && data.trim()) return data;
  return fallback;
};

function getTodayLabel() {
  return new Date().toISOString().slice(0, 10);
}

function getTomorrowLabel() {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  return tomorrow.toISOString().slice(0, 10);
}

function normalize(value) {
  return (value || "").toString().trim();
}

function getPlanEntryTypeLabel(entryType) {
  if (entryType === "NEW_INSTALLATION") return "New installation";
  if (entryType === "NO_PENDING_COMPLAINT") return "No pending complaint";
  return "Complaint";
}

function calculateAge(dateValue) {
  if (!dateValue) return "-";
  const start = new Date(dateValue);
  if (Number.isNaN(start.getTime())) return "-";
  const today = new Date();
  start.setHours(0, 0, 0, 0);
  today.setHours(0, 0, 0, 0);
  return Math.max(0, Math.floor((today - start) / (1000 * 60 * 60 * 24)));
}

function getComplaintAge(complaint, calculateComplaintAgingDays) {
  const serverAge = Number(complaint.complaintAge);
  if (Number.isFinite(serverAge)) return serverAge;

  const calculatedAge = calculateComplaintAgingDays?.(complaint.date);
  if (Number.isFinite(calculatedAge)) return calculatedAge;

  const explicitAge = Number(complaint.agingDays);
  return Number.isFinite(explicitAge) ? explicitAge : -1;
}

function getHardwareDeliveryDate(complaint) {
  return (
    complaint.hardwareReceivedOutwardDate ||
    complaint.receivedOutwardDate ||
    complaint.hardwareLogs?.find((log) => log?.receivedOutwardDate)?.receivedOutwardDate ||
    ""
  );
}

function getHardwareDeliveryAge(complaint) {
  const serverAge = Number(complaint.hardwareDeliveryAge);
  if (Number.isFinite(serverAge)) return serverAge;

  const deliveryDate = getHardwareDeliveryDate(complaint);
  const age = calculateAge(deliveryDate);
  return typeof age === "number" ? age : -1;
}

function getAgePriority(age) {
  if (age >= 5) return "urgent";
  if (age >= 2) return "attention";
  if (age >= 0) return "normal";
  return "unknown";
}

function getVisitPriority(complaint, calculateComplaintAgingDays) {
  if (complaint.priorityType || complaint.priorityLabel) {
    return {
      score: Number(complaint.priorityScore) || 0,
      type: complaint.priorityType || "normal",
      label: complaint.priorityLabel || "Normal",
      detail: complaint.priorityDetail || "",
      urgent: Boolean(complaint.urgent),
    };
  }

  const complaintAge = getComplaintAge(complaint, calculateComplaintAgingDays);
  const hardwareDeliveryAge = getHardwareDeliveryAge(complaint);
  const delivered = normalize(complaint.complaintStatus).toLowerCase() === "delivered";
  const longPending = complaintAge >= 5;
  let urgent = (delivered && hardwareDeliveryAge >= 5) || complaintAge >= 10;

  let score = Math.max(0, complaintAge) * 2;
  let type = "normal";
  let label = "Normal";
  let detail = complaintAge >= 0 ? `Open ${complaintAge} days` : "No age data";

  if (longPending) {
    type = "longPending";
    label = complaintAge >= 10 ? "Critical pending" : "Long pending";
    detail = `Open ${complaintAge} days`;
    score += complaintAge >= 10 ? 35 : 20;
  }

  if (delivered) {
    const hardwareAge = Math.max(0, hardwareDeliveryAge);
    type = "hardwareInstall";
    label =
      hardwareDeliveryAge >= 5
        ? "Urgent hardware install"
        : hardwareDeliveryAge >= 2
        ? "Hardware install priority"
        : "Hardware install";
    detail =
      hardwareDeliveryAge >= 0
        ? `Delivered ${hardwareDeliveryAge} days ago`
        : "Delivered status";
    score += 50 + hardwareAge * 10;
  }

  if (urgent) {
    type = delivered ? "hardwareInstall" : "longPending";
    score += 50;
  }

  return { score, type, label, detail, urgent };
}

function getVisitorName(complaint) {
  return normalize(complaint.visitorName);
}

function getCourierStatus(complaint) {
  return normalize(complaint.courierStatus);
}

function getComplaintKey(complaint) {
  return complaint.complaintId || complaint.id;
}

const PageHeader = ({
  loading,
  lastReviewedAt,
  planningDate,
  reportLoading,
  onDownloadReport,
  onRefresh,
}) => (
  <header className="visit-plan-header">
    <div>
      <span className="visit-plan-kicker">Next-day scheduling</span>
      <h1>Visit Plan</h1>
      <p>Review eligible open complaints before next-day scheduling.</p>
      <div className="visit-plan-meta" aria-label="Planning dates">
        <span>Today: {getTodayLabel()}</span>
        <span>Planning for: {planningDate || "-"}</span>
        <span>Last review: {lastReviewedAt || "-"}</span>
      </div>
    </div>
    <div className="visit-plan-header-actions">
      <button
        className="visit-plan-secondary"
        onClick={onDownloadReport}
        disabled={reportLoading}
      >
        <FileDownloadOutlinedIcon fontSize="small" />
        {reportLoading ? "Exporting..." : "Export data"}
      </button>
      <button className="visit-plan-primary" onClick={onRefresh} disabled={loading}>
        <RefreshIcon fontSize="small" />
        {loading ? "Refreshing..." : "Refresh Open Complaints"}
      </button>
    </div>
  </header>
);

const WorkflowTabs = ({ activeView, pendingCount, isAdmin, onChange }) => (
  <div className="visit-plan-workflow-tabs" role="tablist" aria-label="Visit plan workflow">
    <button
      className={activeView === "build" ? "is-active" : ""}
      onClick={() => onChange("build")}
      role="tab"
      aria-selected={activeView === "build"}
    >
      Build Plan
    </button>
    <button
      className={activeView === "reports" ? "is-active" : ""}
      onClick={() => onChange("reports")}
      role="tab"
      aria-selected={activeView === "reports"}
    >
      Reports
    </button>
    {isAdmin && (
      <button
        className={activeView === "approvals" ? "is-active" : ""}
        onClick={() => onChange("approvals")}
        role="tab"
        aria-selected={activeView === "approvals"}
      >
        Admin Approvals
        <span>{pendingCount}</span>
      </button>
    )}
  </div>
);

const NewInstallationPanel = ({ visitors, cities, defaultDate, busy, onAdd }) => {
  const [open, setOpen] = useState(false);
  const [entryType, setEntryType] = useState("NEW_INSTALLATION");
  const [visitorId, setVisitorId] = useState("");
  const [scheduleDate, setScheduleDate] = useState(defaultDate || "");
  const [destination, setDestination] = useState("");

  useEffect(() => {
    if (!open) setScheduleDate(defaultDate || "");
  }, [defaultDate, open]);

  const sortedVisitors = useMemo(
    () =>
      [...visitors].sort((left, right) =>
        normalize(left.name).localeCompare(normalize(right.name))
      ),
    [visitors]
  );
  const sortedCities = useMemo(
    () =>
      [...cities].sort((left, right) =>
        normalize(left.name).localeCompare(normalize(right.name))
      ),
    [cities]
  );

  const openForType = (type) => {
    setEntryType(type);
    setDestination("");
    setOpen(true);
  };

  const submit = async (event) => {
    event.preventDefault();
    const added = await onAdd({ entryType, visitorId, scheduleDate, destination });
    if (added) {
      setDestination("");
      setOpen(false);
    }
  };

  return (
    <section className="visit-plan-panel visit-plan-installation">
      <div className="visit-plan-installation-heading">
        <div>
          <AddLocationAltOutlinedIcon fontSize="small" />
          <div>
            <strong>Additional plan activity</strong>
            <span>Add work or record that a visitor has no pending complaint.</span>
          </div>
        </div>
        <div className="visit-plan-installation-types">
          <button
            type="button"
            className={`visit-plan-secondary ${open && entryType === "NEW_INSTALLATION" ? "is-active" : ""}`}
            onClick={() => openForType("NEW_INSTALLATION")}
            aria-pressed={open && entryType === "NEW_INSTALLATION"}
          >
            New installation
          </button>
          <button
            type="button"
            className={`visit-plan-secondary ${open && entryType === "NO_PENDING_COMPLAINT" ? "is-active" : ""}`}
            onClick={() => openForType("NO_PENDING_COMPLAINT")}
            aria-pressed={open && entryType === "NO_PENDING_COMPLAINT"}
          >
            No pending complaint
          </button>
          {open && (
            <button
              type="button"
              className="visit-plan-secondary"
              onClick={() => setOpen(false)}
            >
              Close
            </button>
          )}
        </div>
      </div>
      {open && (
        <form
          className={`visit-plan-installation-form ${entryType === "NO_PENDING_COMPLAINT" ? "is-no-pending" : ""}`}
          onSubmit={submit}
        >
          <label>
            <span>Visitor</span>
            <select
              value={visitorId}
              onChange={(event) => setVisitorId(event.target.value)}
              required
            >
              <option value="">Select visitor</option>
              {sortedVisitors.map((visitor) => (
                <option key={visitor.id} value={visitor.id}>
                  {visitor.name}{visitor.city ? ` - ${visitor.city}` : ""}
                </option>
              ))}
            </select>
          </label>
          <label>
            <span>Visit date</span>
            <input
              type="date"
              value={scheduleDate}
              onChange={(event) => setScheduleDate(event.target.value)}
              required
            />
          </label>
          {entryType === "NEW_INSTALLATION" && (
            <label className="visit-plan-installation-destination">
              <span>Destination</span>
              <select
                value={destination}
                onChange={(event) => setDestination(event.target.value)}
                required
              >
                <option value="">Select destination city</option>
                {sortedCities.map((city) => (
                  <option key={city.id ?? city.name} value={city.name}>
                    {city.name}
                  </option>
                ))}
              </select>
            </label>
          )}
          <button
            type="submit"
            className="visit-plan-primary"
            disabled={
              busy ||
              !visitorId ||
              !scheduleDate ||
              (entryType === "NEW_INSTALLATION" && !destination)
            }
          >
            <AddLocationAltOutlinedIcon fontSize="small" />
            {busy ? "Adding..." : "Add to plan"}
          </button>
        </form>
      )}
    </section>
  );
};

const CurrentPlanPanel = ({ plans, busy, onSubmit, onRemove }) => {
  const [draftStationFilter, setDraftStationFilter] = useState("");
  const planGroups = useMemo(
    () =>
      (Array.isArray(plans) ? plans : [])
        .filter((plan) => Array.isArray(plan.entries) && plan.entries.length > 0)
        .sort((left, right) =>
          normalize(left.scheduleDate).localeCompare(normalize(right.scheduleDate))
        ),
    [plans]
  );
  const entries = useMemo(
    () => planGroups.flatMap((plan) => plan.entries),
    [planGroups]
  );
  const stationOptions = useMemo(
    () =>
      Array.from(
        new Set(
          entries
            .map((entry) => normalize(entry.visitorStation))
            .filter(Boolean)
        )
      ).sort((a, b) => a.localeCompare(b)),
    [entries]
  );
  const visibleGroups = planGroups
    .map((plan) => ({
      ...plan,
      visibleEntries: draftStationFilter
        ? plan.entries.filter(
            (entry) => normalize(entry.visitorStation) === draftStationFilter
          )
        : plan.entries,
    }))
    .filter((plan) => plan.visibleEntries.length > 0);
  const visibleEntryCount = visibleGroups.reduce(
    (total, plan) => total + plan.visibleEntries.length,
    0
  );
  const unsubmittedPlanCount = planGroups.filter(
    (plan) => plan.status !== "SUBMITTED"
  ).length;

  useEffect(() => {
    if (draftStationFilter && !stationOptions.includes(draftStationFilter)) {
      setDraftStationFilter("");
    }
  }, [draftStationFilter, stationOptions]);

  if (!planGroups.length) return null;

  return (
    <section className="visit-plan-panel visit-plan-current">
      <div className="visit-plan-current-summary">
        <div>
          <strong>Planned visits</strong>
          <span>
            {entries.length} visit{entries.length === 1 ? "" : "s"} across {planGroups.length} date{planGroups.length === 1 ? "" : "s"}
          </span>
        </div>
        <button
          className="visit-plan-primary"
          onClick={onSubmit}
          disabled={busy || unsubmittedPlanCount === 0}
        >
          <CheckCircleOutlineIcon fontSize="small" />
          {busy
            ? "Submitting..."
            : unsubmittedPlanCount === 0
            ? "Submitted to admin"
            : unsubmittedPlanCount === 1
            ? "Submit for approval"
            : `Submit ${unsubmittedPlanCount} plans for approval`}
        </button>
      </div>
      {planGroups.some((plan) => plan.status === "SUBMITTED") && (
        <p className="visit-plan-current-note">
          Submitted plans remain editable until admin approval. Changes appear in the admin review automatically.
        </p>
      )}
      <details open>
        <summary>Review planned visits</summary>
        <div className="visit-plan-draft-filter">
          <label>
            <span>Station</span>
            <select
              value={draftStationFilter}
              onChange={(event) => setDraftStationFilter(event.target.value)}
            >
              <option value="">All stations</option>
              {stationOptions.map((station) => (
                <option key={station} value={station}>
                  {station}
                </option>
              ))}
            </select>
          </label>
          <strong>
            {visibleEntryCount} of {entries.length} visit{entries.length === 1 ? "" : "s"}
          </strong>
        </div>
        <div className="visit-plan-date-groups">
          {visibleGroups.map((plan) => (
            <section className="visit-plan-date-group" key={plan.id}>
              <header>
                <div>
                  <strong>{plan.scheduleDate}</strong>
                  <span>{plan.visibleEntries.length} visit{plan.visibleEntries.length === 1 ? "" : "s"}</span>
                </div>
                <span className={`visit-plan-plan-status visit-plan-plan-status--${normalize(plan.status).toLowerCase()}`}>
                  {plan.status}
                </span>
              </header>
              <div className="visit-plan-current-items">
                <div className="visit-plan-current-items-header">
                  <span>Activity</span>
                  <span>Bank</span>
                  <span>Branch Code</span>
                  <span>Branch Name</span>
                  <span>Station</span>
                  <span>Destination</span>
                  <span>Visitor</span>
                  <span>Remove</span>
                </div>
                {plan.visibleEntries.map((entry) => (
                  <div key={entry.id}>
                    <span data-label="Activity">
                      <span className={`visit-plan-entry-type ${entry.entryType === "NEW_INSTALLATION" ? "is-installation" : entry.entryType === "NO_PENDING_COMPLAINT" ? "is-no-pending" : ""}`}>
                        {getPlanEntryTypeLabel(entry.entryType)}
                      </span>
                    </span>
                    <span data-label="Bank">{entry.bankName || "-"}</span>
                    <span data-label="Branch Code">{entry.branchCode || "-"}</span>
                    <span data-label="Branch Name">{entry.branchName || "-"}</span>
                    <span data-label="Station">{entry.visitorStation || "-"}</span>
                    <span data-label="Destination">{entry.routeDestination || entry.city || "-"}</span>
                    <span data-label="Visitor">{entry.visitorName || "-"}</span>
                    <button
                      onClick={() => onRemove(plan.id, entry.id)}
                      disabled={busy}
                      title="Remove from plan"
                      aria-label={`Remove ${entry.complaintId || "new installation"} from plan`}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </button>
                  </div>
                ))}
              </div>
            </section>
          ))}
          {visibleGroups.length === 0 && (
            <div className="visit-plan-current-empty">
              No planned visits for this station.
            </div>
          )}
        </div>
      </details>
    </section>
  );
};

const AdminApprovalPanel = ({
  plans,
  loading,
  busyEntryKey,
  bulkApprovalBusy,
  onApproveItem,
  onRejectItem,
  onApprovePlans,
}) => {
  const [selectedPlanIds, setSelectedPlanIds] = useState([]);
  const selectablePlanIds = useMemo(
    () =>
      plans
        .filter((plan) =>
          (Array.isArray(plan.entries) ? plan.entries : []).some(
            (entry) => entry.approvalStatus === "SUBMITTED"
          )
        )
        .map((plan) => String(plan.id)),
    [plans]
  );
  const allSelected =
    selectablePlanIds.length > 0 &&
    selectablePlanIds.every((planId) => selectedPlanIds.includes(planId));
  const approvalControlsBusy = bulkApprovalBusy || Boolean(busyEntryKey);

  useEffect(() => {
    setSelectedPlanIds((current) =>
      current.filter((planId) => selectablePlanIds.includes(planId))
    );
  }, [selectablePlanIds]);

  const togglePlan = (planId) => {
    const normalizedPlanId = String(planId);
    setSelectedPlanIds((current) =>
      current.includes(normalizedPlanId)
        ? current.filter((id) => id !== normalizedPlanId)
        : [...current, normalizedPlanId]
    );
  };

  const approveSelected = async () => {
    const completed = await onApprovePlans(selectedPlanIds);
    if (completed) setSelectedPlanIds([]);
  };

  return (
  <section className="visit-plan-panel visit-plan-admin-review">
    <div className="visit-plan-panel-header">
      <div className="visit-plan-section-title">
        <CheckCircleOutlineIcon fontSize="small" />
        <h2>Plans waiting for approval</h2>
      </div>
      <div className="visit-plan-admin-bulk-actions">
        <span>{plans.length} pending</span>
        <label>
          <input
            type="checkbox"
            checked={allSelected}
            onChange={() => setSelectedPlanIds(allSelected ? [] : selectablePlanIds)}
            disabled={!selectablePlanIds.length || approvalControlsBusy}
          />
          Select all
        </label>
        <button
          type="button"
          className="visit-plan-primary visit-plan-bulk-approve"
          onClick={approveSelected}
          disabled={!selectedPlanIds.length || approvalControlsBusy}
        >
          <CheckCircleOutlineIcon fontSize="small" />
          {bulkApprovalBusy
            ? "Approving..."
            : `Approve selected (${selectedPlanIds.length})`}
        </button>
      </div>
    </div>
    {loading ? (
      <div className="visit-plan-loading">Loading submitted plans...</div>
    ) : plans.length === 0 ? (
      <div className="visit-plan-empty">No plans are waiting for approval.</div>
    ) : (
      <div className="visit-plan-admin-list">
        {plans.map((plan) => {
          const entries = Array.isArray(plan.entries) ? plan.entries : [];
          const planId = String(plan.id);
          const selectable = entries.some((entry) => entry.approvalStatus === "SUBMITTED");
          return (
            <article key={plan.id}>
              <header>
                <div className="visit-plan-admin-plan-heading">
                  <label className="visit-plan-admin-plan-select" title="Select this plan">
                    <input
                      type="checkbox"
                      checked={selectedPlanIds.includes(planId)}
                      onChange={() => togglePlan(planId)}
                      disabled={!selectable || approvalControlsBusy}
                      aria-label={`Select visit plan for ${plan.scheduleDate}`}
                    />
                  </label>
                  <div>
                    <strong>{plan.scheduleDate}</strong>
                    <span>Created by {plan.createdBy} / {entries.length} visits</span>
                  </div>
                </div>
                <span className="visit-plan-plan-status visit-plan-plan-status--submitted">
                  Review individually
                </span>
              </header>
              <div className="visit-plan-admin-items">
                <div className="visit-plan-admin-items-header">
                  <span>Activity</span>
                  <span>Bank</span>
                  <span>Branch Code</span>
                  <span>Branch Name</span>
                  <span>Station</span>
                  <span>Destination</span>
                  <span>Visitor</span>
                  <span>Decision</span>
                </div>
                {entries.map((entry) => {
                  const entryKey = `${plan.id}:${entry.id}`;
                  const busy = bulkApprovalBusy || busyEntryKey === entryKey;
                  const pending = entry.approvalStatus === "SUBMITTED";
                  return (
                    <div key={entry.id}>
                      <span data-label="Activity">
                        <span className={`visit-plan-entry-type ${entry.entryType === "NEW_INSTALLATION" ? "is-installation" : entry.entryType === "NO_PENDING_COMPLAINT" ? "is-no-pending" : ""}`}>
                          {getPlanEntryTypeLabel(entry.entryType)}
                        </span>
                      </span>
                      <span data-label="Bank">{entry.bankName || "-"}</span>
                      <span data-label="Branch Code">{entry.branchCode || "-"}</span>
                      <span data-label="Branch Name">{entry.branchName || "-"}</span>
                      <span data-label="Station">{entry.visitorStation || "-"}</span>
                      <span data-label="Destination">{entry.routeDestination || entry.city || "-"}</span>
                      <span data-label="Visitor">{entry.visitorName || "-"}</span>
                      <span className="visit-plan-admin-actions" data-label="Decision">
                        {pending ? (
                          <>
                            <button
                              className="visit-plan-icon-reject"
                              onClick={() => onRejectItem(plan.id, entry.id)}
                              disabled={busy}
                              title="Reject visit"
                            >
                              <CloseIcon fontSize="small" />
                            </button>
                            <button
                              className="visit-plan-icon-approve"
                              onClick={() => onApproveItem(plan.id, entry.id)}
                              disabled={busy}
                              title="Approve visit"
                            >
                              <CheckCircleOutlineIcon fontSize="small" />
                            </button>
                          </>
                        ) : (
                          <span className={`visit-plan-entry-decision visit-plan-entry-decision--${normalize(entry.approvalStatus).toLowerCase()}`}>
                            {entry.approvalStatus}
                          </span>
                        )}
                      </span>
                    </div>
                  );
                })}
              </div>
            </article>
          );
        })}
      </div>
    )}
  </section>
  );
};

const VisitPlanAccessPanel = ({ users, loading, busyUserId, onToggle }) => (
  <section className="visit-plan-panel visit-plan-access">
    <details>
      <summary>
        <span>Visit Plan user access</span>
        <span>{users.filter((user) => user.enabled).length} enabled</span>
      </summary>
      {loading ? (
        <div className="visit-plan-loading">Loading users...</div>
      ) : (
        <div className="visit-plan-access-list">
          <div className="visit-plan-access-header">
            <span>User</span>
            <span>Role</span>
            <span>Access</span>
          </div>
          {users.map((user) => (
            <label key={user.id}>
              <span>{user.username}</span>
              <span>{user.role}</span>
              <input
                type="checkbox"
                checked={Boolean(user.enabled)}
                disabled={!user.editable || busyUserId === user.id}
                onChange={(event) => onToggle(user.id, event.target.checked)}
                aria-label={`Visit Plan access for ${user.username}`}
              />
            </label>
          ))}
        </div>
      )}
    </details>
  </section>
);

const FilterField = ({ label, children, className = "" }) => (
  <label className={`visit-plan-filter-field ${className}`}>
    <span>{label}</span>
    {children}
  </label>
);

const ExcludedCitiesFilter = ({ cities, selectedCities, onToggle, onToggleAll }) => {
  const selected = Array.isArray(selectedCities) ? selectedCities : [];
  const allSelected = cities.length > 0 && cities.every((city) => selected.includes(city));

  return (
    <div className="visit-plan-filter-field">
      <span>Exclude cities</span>
      <details className="visit-plan-city-exclusions">
        <summary>
          {selected.length ? `${selected.length} cities excluded` : "No excluded cities"}
        </summary>
        <div className="visit-plan-city-exclusions-menu">
          <label className="visit-plan-city-exclusions-all">
            <input
              type="checkbox"
              checked={allSelected}
              onChange={() => onToggleAll(!allSelected)}
            />
            <span>Select all cities</span>
          </label>
          {cities.map((city) => (
            <label key={city}>
              <input
                type="checkbox"
                checked={selected.includes(city)}
                onChange={() => onToggle(city)}
              />
              <span>{city}</span>
            </label>
          ))}
        </div>
      </details>
    </div>
  );
};

const PlanningControls = ({
  filters,
  filterOptions,
  routeSettings,
  planningDate,
  routeLoading,
  routeSuggestions,
  complaintsByCity,
  filteredCount,
  sourceTotal,
  routeError,
  selectedRouteCity,
  onChange,
  onToggleExcludedCity,
  onToggleAllExcludedCities,
  onPlanningDateChange,
  onClear,
  onRouteSettingChange,
  onSuggestRoutes,
  onSelectRoute,
  onReviewRoute,
}) => {
  const [showMoreFilters, setShowMoreFilters] = useState(false);
  const selectedSuggestion = routeSuggestions.find(
    (suggestion) => suggestion.destinationCity === selectedRouteCity
  );
  const activeMoreFilters = [
    filters.assignment !== "all",
    filters.priority,
    filters.visitor,
    filters.status,
    filters.courierStatus,
    filters.bank,
    filters.city,
    filters.excludedCities?.length > 0,
  ].filter(Boolean).length;

  return (
    <section className="visit-plan-panel visit-plan-filter-panel">
      <div className="visit-plan-panel-header">
        <div className="visit-plan-section-title">
          <span className="visit-plan-step">1</span>
          <h2>Select station and complaints</h2>
        </div>
        <div className="visit-plan-control-actions">
          <button
            className="visit-plan-secondary"
            onClick={() => setShowMoreFilters((current) => !current)}
            aria-expanded={showMoreFilters}
          >
            <FilterListIcon fontSize="small" />
            {showMoreFilters ? "Fewer filters" : "More filters"}
            {activeMoreFilters ? ` (${activeMoreFilters})` : ""}
          </button>
          <button className="visit-plan-secondary" onClick={onClear}>
            Clear
          </button>
        </div>
      </div>

      <div className="visit-plan-filters visit-plan-filters--primary">
        <FilterField label="Support station" className="visit-plan-filter-field--start">
          <select name="station" value={filters.station} onChange={onChange}>
            <option value="">Select station</option>
            <option value={UNDETERMINED_STATION}>Station not determined</option>
            {filterOptions.stations.map((station) => (
              <option key={station} value={station}>
                {station}
              </option>
            ))}
          </select>
        </FilterField>
        <FilterField label="Visit date">
          <input
            type="date"
            value={planningDate}
            onChange={(event) => onPlanningDateChange(event.target.value)}
          />
        </FilterField>
        <FilterField label="Search" className="visit-plan-filter-field--search">
          <input
            name="search"
            value={filters.search}
            onChange={onChange}
            placeholder="Complaint ID, branch, bank or city"
          />
        </FilterField>
        <FilterField label="Review order">
          <select name="reviewOrder" value={filters.reviewOrder} onChange={onChange}>
            <option value="recommended">Recommended first</option>
            <option value="agedFirst">Oldest first</option>
            <option value="newestFirst">Newest first</option>
            <option value="default">Default order</option>
          </select>
        </FilterField>
      </div>

      {showMoreFilters && (
        <div className="visit-plan-filters visit-plan-filters--more">
          <FilterField label="Assignment">
            <select name="assignment" value={filters.assignment} onChange={onChange}>
              <option value="all">All assignments</option>
              <option value="assigned">Assigned only</option>
              <option value="unassigned">Missing visitor only</option>
            </select>
          </FilterField>
          <FilterField label="Priority">
            <select name="priority" value={filters.priority} onChange={onChange}>
              <option value="">All priorities</option>
              <option value="slaDue">Deadline priority</option>
              <option value="hardwareInstall">Hardware install</option>
              <option value="longPending">Long pending</option>
              <option value="urgent">Urgent only</option>
            </select>
          </FilterField>
          <FilterField label="Visitor">
            <select name="visitor" value={filters.visitor} onChange={onChange}>
              <option value="">All visitors</option>
              {filterOptions.visitors.map((visitor) => (
                <option key={visitor} value={visitor}>{visitor}</option>
              ))}
            </select>
          </FilterField>
          <FilterField label="Status">
            <select name="status" value={filters.status} onChange={onChange}>
              <option value="">All open statuses</option>
              {filterOptions.statuses.map((status) => (
                <option key={status} value={status}>{status}</option>
              ))}
            </select>
          </FilterField>
          <FilterField label="Courier status">
            <select name="courierStatus" value={filters.courierStatus} onChange={onChange}>
              <option value="">All courier statuses</option>
              {filterOptions.courierStatuses.map((status) => (
                <option key={status} value={status}>{status}</option>
              ))}
            </select>
          </FilterField>
          <FilterField label="Bank">
            <select name="bank" value={filters.bank} onChange={onChange}>
              <option value="">All banks</option>
              {filterOptions.banks.map((bank) => (
                <option key={bank} value={bank}>{bank}</option>
              ))}
            </select>
          </FilterField>
          <FilterField label="City">
            <select name="city" value={filters.city} onChange={onChange}>
              <option value="">All cities</option>
              {filterOptions.cities.map((city) => (
                <option key={city} value={city}>{city}</option>
              ))}
            </select>
          </FilterField>
          <ExcludedCitiesFilter
            cities={filterOptions.cities}
            selectedCities={filters.excludedCities}
            onToggle={onToggleExcludedCity}
            onToggleAll={onToggleAllExcludedCities}
          />
        </div>
      )}

      <div className="visit-plan-route-step">
        <div className="visit-plan-route-step-header">
          <div className="visit-plan-section-title">
            <span className="visit-plan-step">2</span>
            <div>
              <h2>Compare route options</h2>
              <span className="visit-plan-section-count">
                {filteredCount} eligible from {sourceTotal} open-family
              </span>
            </div>
          </div>
          <button
            className="visit-plan-secondary"
            onClick={onSuggestRoutes}
            disabled={
              routeLoading ||
              !filters.station ||
              filters.station === UNDETERMINED_STATION ||
              !filteredCount
            }
          >
            <MapOutlinedIcon fontSize="small" />
            {routeLoading ? "Checking routes..." : "Find routes"}
          </button>
        </div>

        <div className="visit-plan-route-settings">
          <label>
            <span>Nearby time</span>
            <input
              type="number"
              name="maxExtraMinutes"
              min="0"
              step="10"
              value={routeSettings.maxExtraMinutes}
              onChange={onRouteSettingChange}
            />
            <em>min</em>
          </label>
          <label>
            <span>Nearby distance</span>
            <input
              type="number"
              name="maxExtraKm"
              min="0"
              step="10"
              value={routeSettings.maxExtraKm}
              onChange={onRouteSettingChange}
            />
            <em>km</em>
          </label>
        </div>

        {routeError ? (
          <div className="visit-plan-route-state visit-plan-route-state--error">{routeError}</div>
        ) : routeLoading ? (
          <div className="visit-plan-route-state">Checking Google Maps...</div>
        ) : !filters.station ? (
          <div className="visit-plan-route-state">Select a support station above.</div>
        ) : filters.station === UNDETERMINED_STATION ? (
          <div className="visit-plan-route-state">
            Assign a recommended station and visitor before finding routes.
          </div>
        ) : routeSuggestions.length ? (
          <div className="visit-plan-route-picker">
            <label>
              <span>Route suggestion</span>
              <select
                value={selectedRouteCity}
                onChange={(event) => onSelectRoute(event.target.value)}
              >
                <option value="">Select a route to review</option>
                {routeSuggestions.map((suggestion) => {
                  const hasDistance =
                    suggestion.durationMinutesFromStation != null &&
                    suggestion.distanceKmFromStation != null;
                  const complaintCount = getRouteComplaintCount(
                    complaintsByCity,
                    suggestion
                  );
                  return (
                    <option key={suggestion.destinationCity} value={suggestion.destinationCity}>
                      {suggestion.destinationCity} - {hasDistance
                        ? `${suggestion.durationMinutesFromStation} min / ${suggestion.distanceKmFromStation} km`
                        : "distance unavailable"} - {complaintCount} complaint{complaintCount === 1 ? "" : "s"}
                    </option>
                  );
                })}
              </select>
            </label>
            {selectedSuggestion && (
              <RouteSuggestionCard
                suggestion={selectedSuggestion}
                station={filters.station}
                complaintsByCity={complaintsByCity}
                onReview={onReviewRoute}
              />
            )}
          </div>
        ) : (
          <div className="visit-plan-route-state">
            Select Find routes to calculate route suggestions.
          </div>
        )}
      </div>
    </section>
  );
};

const StatusBadge = ({ children, type = "open" }) => {
  const badgeType = normalize(type)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

  return (
    <span className={`visit-plan-badge visit-plan-badge--${badgeType || "neutral"}`}>
      {children || "-"}
    </span>
  );
};

const SyncedTableScroller = ({ children }) => {
  const scrollbarRef = useRef(null);
  const tableWrapRef = useRef(null);
  const [contentWidth, setContentWidth] = useState(0);
  const [scrollbarVisible, setScrollbarVisible] = useState(false);
  const [scrollbarBounds, setScrollbarBounds] = useState({ left: 0, width: 0 });

  useEffect(() => {
    const tableWrap = tableWrapRef.current;
    if (!tableWrap) return undefined;
    const updateLayout = () => {
      const bounds = tableWrap.getBoundingClientRect();
      setContentWidth(tableWrap.scrollWidth);
      setScrollbarBounds({ left: Math.max(0, bounds.left), width: bounds.width });
    };
    updateLayout();
    const observer = new ResizeObserver(updateLayout);
    observer.observe(tableWrap);
    const table = tableWrap.querySelector("table");
    if (table) observer.observe(table);
    const visibilityObserver = new IntersectionObserver(
      ([entry]) => setScrollbarVisible(entry.isIntersecting),
      { threshold: 0 }
    );
    visibilityObserver.observe(tableWrap);
    window.addEventListener("resize", updateLayout);
    return () => {
      observer.disconnect();
      visibilityObserver.disconnect();
      window.removeEventListener("resize", updateLayout);
    };
  }, [children]);

  const syncScroll = (source, target) => {
    if (target && Math.abs(target.scrollLeft - source.scrollLeft) > 1) {
      target.scrollLeft = source.scrollLeft;
    }
  };

  return (
    <>
      <div
        className={`visit-plan-table-scrollbar${scrollbarVisible ? " is-visible" : ""}`}
        ref={scrollbarRef}
        style={{ left: `${scrollbarBounds.left}px`, width: `${scrollbarBounds.width}px` }}
        onScroll={(event) => syncScroll(event.currentTarget, tableWrapRef.current)}
        aria-label="Horizontal complaint table scroll"
      >
        <div style={{ width: `${contentWidth}px` }} />
      </div>
      <div
        className="visit-plan-table-wrap"
        ref={tableWrapRef}
        onScroll={(event) => syncScroll(event.currentTarget, scrollbarRef.current)}
      >
        {children}
      </div>
    </>
  );
};

const PriorityBadge = ({ priority }) => (
  <span className={`visit-plan-priority visit-plan-priority--${priority.type}`}>
    <strong>{priority.label}</strong>
    <span>{priority.detail}</span>
  </span>
);

function getRouteComplaints(complaintsByCity, city) {
  return complaintsByCity.get(normalize(city).toLowerCase()) || [];
}

function getRouteComplaintCount(complaintsByCity, suggestion) {
  const destinationComplaints = getRouteComplaints(
    complaintsByCity,
    suggestion.destinationCity
  );
  return new Set([
    ...destinationComplaints.map(getComplaintKey),
    ...(suggestion.nearbyCities || []).flatMap((city) =>
      getRouteComplaints(complaintsByCity, city.city).map(getComplaintKey)
    ),
  ]).size;
}

const RouteComplaintRows = ({ complaints }) => (
  <div className="visit-plan-route-complaints">
    {complaints.slice(0, 4).map((complaint) => (
      <div className="visit-plan-route-complaint" key={getComplaintKey(complaint)}>
        <strong>{complaint.complaintId || "No complaint ID"}</strong>
        <span>
          {complaint.bankName || "-"} / {complaint.branchCode || "-"} / {complaint.branchName || "-"}
        </span>
      </div>
    ))}
    {complaints.length > 4 && (
      <span className="visit-plan-route-more">+{complaints.length - 4} more in the review table</span>
    )}
  </div>
);

const RouteSuggestionCard = ({
  suggestion,
  station,
  complaintsByCity,
  onReview,
}) => {
  const hasDistance =
    suggestion.durationMinutesFromStation != null && suggestion.distanceKmFromStation != null;
  const destinationComplaints = getRouteComplaints(complaintsByCity, suggestion.destinationCity);
  const routeComplaintCount = getRouteComplaintCount(complaintsByCity, suggestion);

  return (
    <article className="visit-plan-route-item visit-plan-route-item--selected">
      <div className="visit-plan-route-item-header">
        <div>
          <span className="visit-plan-route-label">Destination</span>
          <strong>{suggestion.destinationCity}</strong>
        </div>
        <StatusBadge type={hasDistance ? "recommended" : "neutral"}>
          {hasDistance ? "Recommended" : suggestion.distanceStatus || "Check"}
        </StatusBadge>
      </div>

      <div className="visit-plan-route-stats">
        <div>
          <span>From {station}</span>
          <strong>
            {hasDistance
              ? `${suggestion.durationMinutesFromStation} min / ${suggestion.distanceKmFromStation} km`
              : "Distance unavailable"}
          </strong>
        </div>
        <div>
          <span>At destination</span>
          <strong>
            {destinationComplaints.length} complaint{destinationComplaints.length === 1 ? "" : "s"}
          </strong>
        </div>
      </div>

      {suggestion.resolvedDestinationAddress && (
        <div className="visit-plan-route-resolved">
          Resolved as {suggestion.resolvedDestinationAddress}
        </div>
      )}

      <div className="visit-plan-route-group">
        <div className="visit-plan-route-group-title">
          <strong>{suggestion.destinationCity} complaints</strong>
          <span>{destinationComplaints.length}</span>
        </div>
        <RouteComplaintRows complaints={destinationComplaints} />
      </div>

      {suggestion.nearbyCities?.length > 0 && (
        <div className="visit-plan-route-nearby">
          <span className="visit-plan-route-nearby-title">Nearby complaints on this trip</span>
          {suggestion.nearbyCities.map((city) => {
            const nearbyComplaints = getRouteComplaints(complaintsByCity, city.city);
            return (
              <div className="visit-plan-route-nearby-group" key={city.city}>
                <div className="visit-plan-route-group-title">
                  <strong>{city.city}</strong>
                  <span>
                    {city.durationMinutesFromDestination} min / {nearbyComplaints.length} complaint
                    {nearbyComplaints.length === 1 ? "" : "s"}
                  </span>
                </div>
                <RouteComplaintRows complaints={nearbyComplaints} />
              </div>
            );
          })}
        </div>
      )}

      <a href={suggestion.mapsUrl} target="_blank" rel="noreferrer">
        <OpenInNewIcon fontSize="small" />
        Open Maps
      </a>
      <button
        className="visit-plan-route-select"
        onClick={onReview}
      >
        Review {routeComplaintCount} complaint{routeComplaintCount === 1 ? "" : "s"}
        <ArrowForwardIcon fontSize="small" />
      </button>
    </article>
  );
};

const ComplaintReviewTable = ({
  complaints,
  totalCount,
  sourceTotal,
  visibleLimit,
  planningForDate,
  loading,
  visitorsLoading,
  savingAssignments,
  approvingComplaints,
  plannedEntriesByComplaint,
  getScheduleDateForComplaint,
  onScheduleDateChange,
  calculateAgingDays,
  getAssignedVisitorStation,
  getVisitorOptionsForComplaint,
  assignVisitorToComplaint,
  nearestStationLookups,
  preferredStations,
  findNearestStations,
  selectPreferredStation,
  saveComplaintToPlan,
  onShowMore,
  selectedRoute,
  selectedRouteOrigin,
  onClearRoute,
}) => (
  <section className="visit-plan-panel visit-plan-review-panel" id="visit-plan-review">
    <div className="visit-plan-panel-header">
      <div className="visit-plan-section-title">
        <span className="visit-plan-step">3</span>
        <h2>Review and add complaints to the plan</h2>
      </div>
      <span>{totalCount} eligible from {sourceTotal} open-family</span>
    </div>

    {selectedRoute && (
      <div className="visit-plan-review-route">
        <div>
          <MapOutlinedIcon fontSize="small" />
          <span>
            <strong>{selectedRouteOrigin} to {selectedRoute.destinationCity}</strong>
            {totalCount} complaint{totalCount === 1 ? "" : "s"} across{" "}
            {1 + (selectedRoute.nearbyCities?.length || 0)} cities
          </span>
        </div>
        <button className="visit-plan-secondary" onClick={onClearRoute}>
          Show all complaints
        </button>
      </div>
    )}

    {loading ? (
      <div className="visit-plan-loading">Loading extracted complaints...</div>
    ) : (
      <>
        <SyncedTableScroller>
          <table className="visit-plan-table">
            <thead>
              <tr>
                <th>S.No</th>
                <th>Date</th>
                <th>Complaint Age</th>
                <th>HW Delivery Age</th>
                <th>Priority Reason</th>
                <th>Bank</th>
                <th>Branch</th>
                <th>City</th>
                <th>Status</th>
                <th>Courier Status</th>
                <th>Visitor Name</th>
                <th>Plan</th>
                <th>Complaint ID</th>
              </tr>
            </thead>
            <tbody>
              {complaints.map((complaint, index) => {
                const visitor = getVisitorName(complaint);
                const visitorStation = getAssignedVisitorStation(complaint);
                const age = getComplaintAge(complaint, calculateAgingDays);
                const agePriority = getAgePriority(age);
                const hardwareDeliveryDate = getHardwareDeliveryDate(complaint);
                const hardwareDeliveryAge = getHardwareDeliveryAge(complaint);
                const hardwareAgePriority = getAgePriority(hardwareDeliveryAge);
                const visitPriority = getVisitPriority(complaint, calculateAgingDays);
                const visitorOptions = getVisitorOptionsForComplaint(complaint);
                const selectedVisitorOption = visitorOptions.find(
                  (item) =>
                    (complaint.visitorId != null &&
                      String(item.id) === String(complaint.visitorId)) ||
                    normalize(item.name).toLowerCase() === visitor.toLowerCase()
                );
                const complaintKey = getComplaintKey(complaint);
                const nearestLookup = nearestStationLookups[complaintKey];
                const preferredStation = preferredStations[complaintKey] || "";
                const scheduleDate = getScheduleDateForComplaint(complaint);
                const hasStationVisitors =
                  visitorOptions.length > 0 &&
                  visitorOptions.some(
                    (item) =>
                      normalize(item.city).toLowerCase() ===
                      normalize(complaint.city).toLowerCase()
                  );
                const saving = Boolean(savingAssignments[complaintKey]);
                const approving = Boolean(approvingComplaints[complaintKey]);
                const plannedEntry = plannedEntriesByComplaint.get(
                  normalize(complaint.complaintId).toLowerCase()
                );
                const canSave = Boolean(visitor) && !saving && !approving;

                return (
                  <tr key={complaintKey}>
                    <td>{index + 1}</td>
                    <td>{complaint.date || "-"}</td>
                    <td>
                      <span className={`visit-plan-age visit-plan-age--${agePriority}`}>
                        {age >= 0 ? age : "-"}
                      </span>
                    </td>
                    <td>
                      {hardwareDeliveryAge >= 0 ? (
                        <span className="visit-plan-hardware-age">
                          <span
                            className={`visit-plan-age visit-plan-age--${hardwareAgePriority}`}
                          >
                            {hardwareDeliveryAge}
                          </span>
                          <span className="visit-plan-subtle">{hardwareDeliveryDate}</span>
                        </span>
                      ) : (
                        <span className="visit-plan-subtle">-</span>
                      )}
                    </td>
                    <td>
                      <PriorityBadge priority={visitPriority} />
                    </td>
                    <td>{complaint.bankName || "-"}</td>
                    <td>
                      <span className="visit-plan-branch">
                        <strong>{complaint.branchCode || "-"}</strong>
                        <span className="visit-plan-subtle" title={complaint.branchName || "-"}>
                          {complaint.branchName || "-"}
                        </span>
                      </span>
                    </td>
                    <td>{complaint.city || "-"}</td>
                    <td>
                      <StatusBadge type={normalize(complaint.complaintStatus).toLowerCase()}>
                        {complaint.complaintStatus || "Open"}
                      </StatusBadge>
                    </td>
                    <td>
                      <StatusBadge type="courier">{getCourierStatus(complaint) || "N/A"}</StatusBadge>
                    </td>
                    <td>
                      <div className="visit-plan-assignment">
                        <select
                          value={selectedVisitorOption?.id || ""}
                          onChange={(event) =>
                            assignVisitorToComplaint(complaint, event.target.value)
                          }
                          disabled={visitorsLoading || saving || !visitorOptions.length}
                          aria-label={`Visitor for ${complaint.complaintId || "complaint"}`}
                        >
                          <option value="">
                            {saving
                              ? "Saving..."
                              : visitorsLoading
                              ? "Loading visitors..."
                              : "Assign visitor"}
                          </option>
                          {visitorOptions.map((item) => (
                            <option key={item.id} value={item.id}>
                              {item.name} - {item.city || "No City"}
                            </option>
                          ))}
                        </select>
                        <span
                          className={
                            visitor || hasStationVisitors
                              ? "visit-plan-assignment-hint"
                              : "visit-plan-assignment-hint visit-plan-assignment-hint--fallback"
                          }
                        >
                          {saving
                            ? "Updating assignment..."
                            : visitor
                            ? `Assigned / ${visitorStation || "No station"}`
                            : preferredStation
                            ? `Suggested station / ${preferredStation}`
                            : hasStationVisitors
                            ? "Station visitors"
                            : "Station not determined"}
                        </span>
                        {!visitorStation && !hasStationVisitors && (
                          <div className="visit-plan-nearest-stations">
                            {!nearestLookup?.stations?.length && !nearestLookup?.error && (
                              <button
                                type="button"
                                className="visit-plan-nearest-trigger"
                                onClick={() => findNearestStations(complaint)}
                                disabled={nearestLookup?.loading || !complaint.city}
                              >
                                <MapOutlinedIcon fontSize="inherit" />
                                {nearestLookup?.loading ? "Checking..." : "Find nearest stations"}
                              </button>
                            )}
                            {nearestLookup?.error && (
                              <button
                                type="button"
                                className="visit-plan-nearest-error"
                                onClick={() => findNearestStations(complaint)}
                              >
                                {nearestLookup.error} Retry
                              </button>
                            )}
                            {nearestLookup?.stations?.length > 0 && (
                              <div className="visit-plan-nearest-options">
                                {nearestLookup.stations.map((station) => (
                                  <button
                                    type="button"
                                    key={station.stationCity}
                                    className={
                                      preferredStation === station.stationCity ? "is-selected" : ""
                                    }
                                    onClick={() =>
                                      selectPreferredStation(complaint, station.stationCity)
                                    }
                                    title={
                                      station.durationMinutes != null
                                        ? `${station.durationMinutes} minutes by road`
                                        : "Approximate coordinate distance"
                                    }
                                  >
                                    <span>{station.stationCity}</span>
                                    <strong>
                                      {station.distanceKm} km
                                      {station.distanceSource === "STRAIGHT_LINE" ? " approx." : ""}
                                    </strong>
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </td>
                    <td>
                      <div className="visit-plan-row-plan">
                        <input
                          type="date"
                          value={scheduleDate}
                          onChange={(event) =>
                            onScheduleDateChange(complaint, event.target.value)
                          }
                          aria-label={`Schedule date for ${complaint.complaintId || "complaint"}`}
                        />
                        <button
                          className={`visit-plan-secondary visit-plan-approve ${
                            plannedEntry
                              ? "visit-plan-approve--update"
                              : "visit-plan-approve--add"
                          }`}
                          onClick={() => saveComplaintToPlan(complaint)}
                          disabled={!canSave || !scheduleDate}
                          title={
                            visitor
                              ? `${plannedEntry ? "Update" : "Add to"} plan for ${scheduleDate || planningForDate}`
                            : "Assign a visitor before adding to plan"
                          }
                        >
                          {approving
                            ? "Saving..."
                            : visitor
                            ? plannedEntry
                              ? "Update plan"
                              : "Add to plan"
                            : "Assign first"}
                        </button>
                      </div>
                    </td>
                    <td>
                      <span className="visit-plan-complaint-id">
                        {complaint.complaintId || "-"}
                      </span>
                    </td>
                  </tr>
                );
              })}
              {!totalCount && (
                <tr>
                  <td colSpan="13" className="visit-plan-empty">
                    No open complaints match the current filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </SyncedTableScroller>
        {totalCount > visibleLimit && (
          <div className="visit-plan-table-footer">
            <span>
              Showing {complaints.length} of {totalCount}
            </span>
            <button className="visit-plan-secondary" onClick={onShowMore}>
              Show more
            </button>
          </div>
        )}
      </>
    )}
  </section>
);

const VisitPlan = ({ calculateAgingDays, initialWorkflowView = "build" }) => {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const isAdmin = localStorage.getItem("role") === "ADMIN";
  const [openComplaints, setOpenComplaints] = useState([]);
  const [reviewSourceTotal, setReviewSourceTotal] = useState(0);
  const [visitors, setVisitors] = useState([]);
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [visitorsLoading, setVisitorsLoading] = useState(false);
  const [savingAssignments, setSavingAssignments] = useState({});
  const [approvingComplaints, setApprovingComplaints] = useState({});
  const [error, setError] = useState("");
  const [assignmentError, setAssignmentError] = useState("");
  const [planLoadError, setPlanLoadError] = useState("");
  const [reportError, setReportError] = useState("");
  const [reportLoading, setReportLoading] = useState(false);
  const [routeError, setRouteError] = useState("");
  const [routeLoading, setRouteLoading] = useState(false);
  const [routeSuggestions, setRouteSuggestions] = useState([]);
  const [selectedRouteCity, setSelectedRouteCity] = useState("");
  const [nearestStationLookups, setNearestStationLookups] = useState({});
  const [preferredStations, setPreferredStations] = useState({});
  const [routeSettings, setRouteSettings] = useState({
    maxExtraMinutes: 60,
    maxExtraKm: 500,
  });
  const [lastReviewedAt, setLastReviewedAt] = useState("");
  const [planningDate, setPlanningDate] = useState(getTomorrowLabel);
  const [complaintScheduleDates, setComplaintScheduleDates] = useState({});
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [visibleLimit, setVisibleLimit] = useState(INITIAL_TABLE_LIMIT);
  const [editablePlans, setEditablePlans] = useState([]);
  const [planBusy, setPlanBusy] = useState(false);
  const [installationBusy, setInstallationBusy] = useState(false);
  const [pendingPlans, setPendingPlans] = useState([]);
  const [pendingPlansLoading, setPendingPlansLoading] = useState(false);
  const [busyEntryKey, setBusyEntryKey] = useState("");
  const [bulkApprovalBusy, setBulkApprovalBusy] = useState(false);
  const [activeWorkflowView, setActiveWorkflowView] = useState(initialWorkflowView);
  const [accessUsers, setAccessUsers] = useState([]);
  const [accessUsersLoading, setAccessUsersLoading] = useState(false);
  const [busyAccessUserId, setBusyAccessUserId] = useState(null);

  const fetchVisitors = useCallback(async () => {
    setVisitorsLoading(true);

    try {
      const response = await axios.get(`${API_BASE_URL}/data/visitors`, {
        withCredentials: true,
      });
      setVisitors(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error("Visit plan visitors fetch failed:", err);
      setVisitors([]);
      setError("Could not load visitors for assignment.");
    } finally {
      setVisitorsLoading(false);
    }
  }, [API_BASE_URL]);

  const fetchCities = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/data/cities`, {
        withCredentials: true,
      });
      setCities(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error("Visit plan cities fetch failed:", err);
      setCities([]);
      setError("Could not load installation destinations.");
    }
  }, [API_BASE_URL]);

  const fetchOpenComplaints = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const response = await axios.get(`${API_BASE_URL}/visit-plan/review`, {
        params: { size: PAGE_SIZE },
        withCredentials: true,
      });
      const complaints = Array.isArray(response.data?.complaints)
        ? response.data.complaints
        : [];

      setOpenComplaints(complaints);
      setReviewSourceTotal(Number(response.data?.sourceTotal) || complaints.length);
      setLastReviewedAt(
        response.data?.reviewedAt
          ? new Date(response.data.reviewedAt).toLocaleString()
          : new Date().toLocaleString()
      );
    } catch (err) {
      console.error("Visit plan open complaints fetch failed:", err);
      setOpenComplaints([]);
      setReviewSourceTotal(0);
      setError("Could not extract the latest open complaints.");
    } finally {
      setLoading(false);
    }
  }, [API_BASE_URL]);

  const fetchEditablePlans = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/visit-plan/plans/mine/editable`, {
        withCredentials: true,
      });
      setEditablePlans(Array.isArray(response.data) ? response.data : []);
      setPlanLoadError("");
    } catch (err) {
      console.error("Editable visit plans fetch failed:", err);
      setPlanLoadError("Could not load your visit plans.");
    }
  }, [API_BASE_URL]);

  const fetchPendingPlans = useCallback(async () => {
    if (!isAdmin) return;
    setPendingPlansLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/visit-plan/plans/pending`, {
        withCredentials: true,
      });
      setPendingPlans(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error("Pending visit plans fetch failed:", err);
      setAssignmentError("Could not load plans waiting for admin approval.");
    } finally {
      setPendingPlansLoading(false);
    }
  }, [API_BASE_URL, isAdmin]);

  const fetchAccessUsers = useCallback(async () => {
    if (!isAdmin) return;
    setAccessUsersLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/admin/visit-plan-access`, {
        withCredentials: true,
      });
      setAccessUsers(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error("Visit Plan access users fetch failed:", err);
      setAssignmentError("Could not load Visit Plan user access.");
    } finally {
      setAccessUsersLoading(false);
    }
  }, [API_BASE_URL, isAdmin]);

  useEffect(() => {
    fetchOpenComplaints();
    fetchVisitors();
    fetchCities();
  }, [fetchCities, fetchOpenComplaints, fetchVisitors]);

  useEffect(() => {
    fetchEditablePlans();
  }, [fetchEditablePlans]);

  useEffect(() => {
    fetchPendingPlans();
    fetchAccessUsers();
  }, [fetchAccessUsers, fetchPendingPlans]);
  useEffect(() => {
    setActiveWorkflowView(initialWorkflowView);
    if (initialWorkflowView === "approvals") {
      fetchPendingPlans();
    }
  }, [fetchPendingPlans, initialWorkflowView]);

  const visitorMaps = useMemo(() => {
    const byId = new Map();
    const byName = new Map();

    visitors.forEach((visitor) => {
      if (visitor.id !== undefined && visitor.id !== null) {
        byId.set(String(visitor.id), visitor);
      }

      const name = normalize(visitor.name).toLowerCase();
      if (name) byName.set(name, visitor);
    });

    return { byId, byName };
  }, [visitors]);

  const getAssignedVisitorRecord = useCallback(
    (complaint) => {
      if (complaint.visitorId !== undefined && complaint.visitorId !== null) {
        const byIdMatch = visitorMaps.byId.get(String(complaint.visitorId));
        if (byIdMatch) return byIdMatch;
      }

      const visitorName = getVisitorName(complaint).toLowerCase();
      return visitorName ? visitorMaps.byName.get(visitorName) : null;
    },
    [visitorMaps]
  );

  const getAssignedVisitorStation = useCallback(
    (complaint) => normalize(getAssignedVisitorRecord(complaint)?.city),
    [getAssignedVisitorRecord]
  );

  const filterOptions = useMemo(() => {
    const unique = (getter) =>
      Array.from(new Set(openComplaints.map(getter).map(normalize).filter(Boolean))).sort();

    return {
      visitors: unique(getVisitorName),
      stations: Array.from(
        new Set(visitors.map((visitor) => normalize(visitor.city)).filter(Boolean))
      ).sort(),
      statuses: unique((item) => item.complaintStatus),
      courierStatuses: unique(getCourierStatus),
      banks: unique((item) => item.bankName),
      cities: unique((item) => item.city),
    };
  }, [openComplaints, visitors]);

  const filteredComplaints = useMemo(() => {
    const search = filters.search.toLowerCase().trim();
    const excludedCities = new Set(
      (filters.excludedCities || []).map((city) => normalize(city).toLowerCase())
    );

    const filtered = openComplaints.filter((item) => {
      const visitor = getVisitorName(item);
      const visitorStation = getAssignedVisitorStation(item);
      const text = [
        item.complaintId,
        item.bankName,
        item.branchCode,
        item.branchName,
        item.city,
        item.visitorName,
        visitorStation,
        getHardwareDeliveryDate(item),
        item.complaintStatus,
        item.courierStatus,
        item.complaintType,
      ]
        .map(normalize)
        .join(" ")
        .toLowerCase();

      const matchesSearch = search ? text.includes(search) : true;
      const matchesVisitor = filters.visitor ? visitor === filters.visitor : true;
      const matchesStatus = filters.status
        ? normalize(item.complaintStatus) === filters.status
        : true;
      const matchesCourierStatus = filters.courierStatus
        ? getCourierStatus(item) === filters.courierStatus
        : true;
      const matchesBank = filters.bank ? normalize(item.bankName) === filters.bank : true;
      const matchesCity = filters.city ? normalize(item.city) === filters.city : true;
      const isExcludedCity = excludedCities.has(normalize(item.city).toLowerCase());
      const matchesStation = filters.station
        ? filters.station === UNDETERMINED_STATION
          ? !visitorStation
          : visitorStation === filters.station
        : true;
      const visitPriority = getVisitPriority(item, calculateAgingDays);
      const matchesPriority =
        filters.priority === "hardwareInstall"
          ? visitPriority.type === "hardwareInstall"
          : filters.priority === "longPending"
          ? visitPriority.type === "longPending"
          : filters.priority === "slaDue"
          ? visitPriority.type === "slaDue"
          : filters.priority === "urgent"
          ? visitPriority.urgent
          : true;
      const matchesAssignment =
        filters.assignment === "assigned"
          ? Boolean(visitor)
          : filters.assignment === "unassigned"
          ? !visitor
          : true;

      return (
        matchesSearch &&
        matchesVisitor &&
        matchesStatus &&
        matchesCourierStatus &&
        matchesBank &&
        matchesCity &&
        !isExcludedCity &&
        matchesStation &&
        matchesPriority &&
        matchesAssignment
      );
    });

    return [...filtered].sort((a, b) => {
      if (filters.reviewOrder === "recommended") {
        const scoreDiff =
          getVisitPriority(b, calculateAgingDays).score -
          getVisitPriority(a, calculateAgingDays).score;
        if (scoreDiff !== 0) return scoreDiff;
      }

      if (filters.reviewOrder === "agedFirst") {
        const ageDiff =
          getComplaintAge(b, calculateAgingDays) -
          getComplaintAge(a, calculateAgingDays);
        if (ageDiff !== 0) return ageDiff;
      }

      if (filters.reviewOrder === "newestFirst") {
        const dateDiff = new Date(b.date || 0) - new Date(a.date || 0);
        if (dateDiff !== 0) return dateDiff;
      }

      return (b.id || 0) - (a.id || 0);
    });
  }, [calculateAgingDays, filters, getAssignedVisitorStation, openComplaints]);

  const selectedRoute = useMemo(
    () =>
      routeSuggestions.find(
        (suggestion) => suggestion.destinationCity === selectedRouteCity
      ) || null,
    [routeSuggestions, selectedRouteCity]
  );

  useEffect(() => {
    if (selectedRouteCity && !selectedRoute) {
      setSelectedRouteCity("");
    }
  }, [selectedRoute, selectedRouteCity]);

  const reviewComplaints = useMemo(() => {
    if (!selectedRoute) return filteredComplaints;

    const routeCities = new Set(
      [
        selectedRoute.destinationCity,
        ...(selectedRoute.nearbyCities || []).map((city) => city.city),
      ].map((city) => normalize(city).toLowerCase())
    );
    return filteredComplaints.filter((complaint) =>
      routeCities.has(normalize(complaint.city).toLowerCase())
    );
  }, [filteredComplaints, selectedRoute]);

  const visibleComplaints = useMemo(
    () => reviewComplaints.slice(0, visibleLimit),
    [reviewComplaints, visibleLimit]
  );

  useEffect(() => {
    setVisibleLimit(INITIAL_TABLE_LIMIT);
  }, [filters, selectedRouteCity]);

  const handleFilterChange = (event) => {
    const { name, value } = event.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
    setSelectedRouteCity("");
    setRouteSuggestions([]);
    setRouteError("");
  };

  const toggleExcludedCity = (city) => {
    setFilters((prev) => {
      const selected = new Set(prev.excludedCities || []);
      if (selected.has(city)) selected.delete(city);
      else selected.add(city);
      return { ...prev, excludedCities: Array.from(selected) };
    });
    setSelectedRouteCity("");
    setRouteSuggestions([]);
    setRouteError("");
  };

  const toggleAllExcludedCities = (shouldSelectAll) => {
    setFilters((prev) => ({
      ...prev,
      excludedCities: shouldSelectAll ? [...filterOptions.cities] : [],
    }));
    setSelectedRouteCity("");
    setRouteSuggestions([]);
    setRouteError("");
  };

  const clearFilters = () => {
    setFilters({ ...EMPTY_FILTERS, excludedCities: [] });
    setRouteSuggestions([]);
    setSelectedRouteCity("");
    setRouteError("");
  };

  const showMoreComplaints = () => {
    setVisibleLimit((prev) => Math.min(prev + TABLE_LIMIT_STEP, reviewComplaints.length));
  };

  const handleRouteSettingChange = (event) => {
    const { name, value } = event.target;
    const numericValue = Math.max(0, Number(value) || 0);
    setRouteSettings((prev) => ({ ...prev, [name]: numericValue }));
    setRouteSuggestions([]);
    setSelectedRouteCity("");
    setRouteError("");
  };

  const selectRouteForReview = (destinationCity) => {
    setSelectedRouteCity(destinationCity);
  };

  const reviewSelectedRoute = () => {
    if (!selectedRouteCity) return;
    window.requestAnimationFrame(() => {
      document.getElementById("visit-plan-review")?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    });
  };

  const complaintsByCity = useMemo(() => {
    return filteredComplaints.reduce((groups, complaint) => {
      const city = normalize(complaint.city).toLowerCase();
      if (!city) return groups;
      if (!groups.has(city)) groups.set(city, []);
      groups.get(city).push(complaint);
      return groups;
    }, new Map());
  }, [filteredComplaints]);

  const fetchRouteSuggestions = useCallback(async ({ quiet = false } = {}) => {
    const stationCity = normalize(filters.station);
    if (!stationCity || stationCity === UNDETERMINED_STATION) {
      if (!quiet) {
        setRouteError(
          stationCity === UNDETERMINED_STATION
            ? "Assign a station and visitor before generating routes."
            : "Select a station first to generate route suggestions."
        );
      }
      return;
    }

    const complaintCities = Array.from(
      new Set(filteredComplaints.map((complaint) => normalize(complaint.city)).filter(Boolean))
    );

    if (!complaintCities.some((city) => city.toLowerCase() !== stationCity.toLowerCase())) {
      setRouteSuggestions([]);
      setRouteError(
        quiet ? "" : "No outside-station complaint cities found in the current view."
      );
      return;
    }

    setRouteLoading(true);
    setRouteError("");

    try {
      const response = await axios.post(
        `${API_BASE_URL}/visit-plan/route-suggestions`,
        {
          stationCity,
          complaintCities,
          maxExtraMinutes: routeSettings.maxExtraMinutes,
          maxExtraKm: routeSettings.maxExtraKm,
        },
        { withCredentials: true }
      );

      setRouteSuggestions(response.data?.suggestions || []);
    } catch (err) {
      console.error("Visit plan route suggestion failed:", err);
      const detail =
        typeof err.response?.data === "string"
          ? err.response.data
          : err.response?.data?.message;
      setRouteSuggestions([]);
      setRouteError(
        detail
          ? `Could not generate route suggestions: ${detail}`
          : "Could not generate route suggestions from Google Maps."
      );
    } finally {
      setRouteLoading(false);
    }
  }, [API_BASE_URL, filteredComplaints, filters.station, routeSettings]);

  const getVisitorOptionsForComplaint = (complaint) => {
    const complaintKey = getComplaintKey(complaint);
    const complaintCity = normalize(complaint.city).toLowerCase();
    const stationVisitors = visitors.filter(
      (visitor) => normalize(visitor.city).toLowerCase() === complaintCity
    );
    const preferredStation = normalize(preferredStations[complaintKey]).toLowerCase();
    const assignedVisitor = getAssignedVisitorRecord(complaint);
    const options = stationVisitors.length
      ? stationVisitors
      : preferredStation
      ? visitors.filter(
          (visitor) => normalize(visitor.city).toLowerCase() === preferredStation
        )
      : assignedVisitor
      ? visitors
      : [];

    if (
      assignedVisitor &&
      !options.some((visitor) => String(visitor.id) === String(assignedVisitor.id))
    ) {
      return [assignedVisitor, ...options];
    }
    return options;
  };

  const findNearestStations = async (complaint) => {
    const complaintKey = getComplaintKey(complaint);
    const city = normalize(complaint.city);
    if (!complaintKey || !city || nearestStationLookups[complaintKey]?.loading) return;

    setNearestStationLookups((previous) => ({
      ...previous,
      [complaintKey]: { loading: true, error: "", stations: [] },
    }));

    try {
      const response = await axios.get(`${API_BASE_URL}/visit-plan/nearest-stations`, {
        params: { city },
        withCredentials: true,
      });
      const stations = Array.isArray(response.data?.stations) ? response.data.stations : [];
      setNearestStationLookups((previous) => ({
        ...previous,
        [complaintKey]: {
          loading: false,
          error: stations.length ? "" : "No station distance found.",
          stations,
        },
      }));
    } catch (err) {
      setNearestStationLookups((previous) => ({
        ...previous,
        [complaintKey]: {
          loading: false,
          error: apiErrorMessage(err, "Could not check stations."),
          stations: [],
        },
      }));
    }
  };

  const selectPreferredStation = (complaint, stationCity) => {
    const complaintKey = getComplaintKey(complaint);
    if (!complaintKey) return;
    setPreferredStations((previous) => ({
      ...previous,
      [complaintKey]: stationCity,
    }));
  };

  const assignVisitorToComplaint = async (complaint, visitorId) => {
    const selectedVisitor = visitors.find(
      (visitor) => String(visitor.id) === String(visitorId)
    );
    const complaintId = complaint.complaintId;

    if (!complaintId || !selectedVisitor) return;

    const key = getComplaintKey(complaint);
    setAssignmentError("");
    setSavingAssignments((prev) => ({ ...prev, [key]: true }));

    try {
      await axios.put(
        `${API_BASE_URL}/complaints/${encodeURIComponent(complaintId)}/assign-to-visitor`,
        null,
        {
          params: { visitorId: selectedVisitor.id },
          withCredentials: true,
        }
      );

      setOpenComplaints((prev) =>
        prev.map((item) =>
          getComplaintKey(item) === key
            ? {
                ...item,
                visitorId: selectedVisitor.id,
                visitorName: selectedVisitor.name,
              }
            : item
        )
      );
      setPreferredStations((previous) => {
        const next = { ...previous };
        delete next[key];
        return next;
      });
      setNearestStationLookups((previous) => {
        const next = { ...previous };
        delete next[key];
        return next;
      });
    } catch (err) {
      console.error("Visit plan visitor assignment failed:", err);
      setAssignmentError(`Could not assign visitor for ${complaintId}.`);
    } finally {
      setSavingAssignments((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  };

  const getScheduleDateForComplaint = useCallback(
    (complaint) => {
      const key = getComplaintKey(complaint);
      return complaintScheduleDates[key] || complaint.scheduleDate || planningDate;
    },
    [complaintScheduleDates, planningDate]
  );

  const handleComplaintScheduleDateChange = (complaint, date) => {
    const key = getComplaintKey(complaint);
    const defaultDate = complaint.scheduleDate || planningDate;
    setComplaintScheduleDates((prev) => {
      const next = { ...prev };
      if (!date || date === defaultDate) {
        delete next[key];
      } else {
        next[key] = date;
      }
      return next;
    });
  };

  const plannedEntriesByComplaint = useMemo(() => {
    const entries = editablePlans.flatMap((plan) =>
      (Array.isArray(plan.entries) ? plan.entries : []).map((entry) => ({
        ...entry,
        planId: plan.id,
        planStatus: plan.status,
      }))
    );
    return new Map(
      entries.map((entry) => [normalize(entry.complaintId).toLowerCase(), entry])
    );
  }, [editablePlans]);

  const updateEditablePlan = (plan) => {
    if (!plan?.id) return;
    setEditablePlans((previous) =>
      [...previous.filter((item) => item.id !== plan.id), plan].sort((left, right) =>
        normalize(left.scheduleDate).localeCompare(normalize(right.scheduleDate))
      )
    );
  };

  const saveComplaintToPlan = async (complaint) => {
    const complaintId = complaint.complaintId;
    const key = getComplaintKey(complaint);
    const scheduleDate = getScheduleDateForComplaint(complaint);

    if (!complaintId || approvingComplaints[key]) return;

    setAssignmentError("");
    setRouteError("");

    if (!scheduleDate) {
      setAssignmentError("Select a schedule date before adding the complaint to a plan.");
      return;
    }

    setApprovingComplaints((prev) => ({ ...prev, [key]: true }));

    try {
      const response = await axios.post(
        `${API_BASE_URL}/visit-plan/plans/items/${encodeURIComponent(complaintId)}`,
        {
          scheduleDate,
          routeOrigin: selectedRoute ? normalize(filters.station) : null,
          routeDestination: selectedRoute?.destinationCity || null,
          routeDistanceKm: selectedRoute?.distanceKmFromStation ?? null,
          routeDurationMinutes: selectedRoute?.durationMinutesFromStation ?? null,
        },
        { withCredentials: true }
      );
      updateEditablePlan(response.data);
    } catch (err) {
      console.error("Saving complaint to visit plan failed:", err);
      const detail =
        typeof err.response?.data === "string"
          ? err.response.data
          : err.response?.data?.message;
      setAssignmentError(
        detail || `Could not add ${complaintId} to the visit plan.`
      );
    } finally {
      setApprovingComplaints((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  };

  const addInstallationToPlan = async ({ entryType, visitorId, scheduleDate, destination }) => {
    if (installationBusy) return false;
    setInstallationBusy(true);
    setAssignmentError("");
    try {
      const response = await axios.post(
        `${API_BASE_URL}/visit-plan/plans/installations`,
        {
          entryType,
          visitorId: Number(visitorId),
          scheduleDate,
          destination: normalize(destination),
        },
        { withCredentials: true }
      );
      updateEditablePlan(response.data);
      return true;
    } catch (err) {
      setAssignmentError(apiErrorMessage(err, "Could not add the installation visit."));
      return false;
    } finally {
      setInstallationBusy(false);
    }
  };

  const submitEditablePlans = async () => {
    const plansToSubmit = editablePlans.filter(
      (plan) =>
        plan.status !== "SUBMITTED" &&
        Array.isArray(plan.entries) &&
        plan.entries.length > 0
    );
    if (!plansToSubmit.length || planBusy) return;

    setPlanBusy(true);
    setAssignmentError("");
    try {
      for (const plan of plansToSubmit) {
        const response = await axios.post(
          `${API_BASE_URL}/visit-plan/plans/${plan.id}/submit`,
          null,
          { withCredentials: true }
        );
        updateEditablePlan(response.data);
      }
      await fetchPendingPlans();
    } catch (err) {
      setAssignmentError(apiErrorMessage(err, "Could not submit all visit plans."));
    } finally {
      setPlanBusy(false);
    }
  };

  const removePlanItem = async (planId, entryId) => {
    if (!planId || planBusy) return;
    setPlanBusy(true);
    setAssignmentError("");
    try {
      const response = await axios.delete(
        `${API_BASE_URL}/visit-plan/plans/${planId}/items/${entryId}`,
        { withCredentials: true }
      );
      updateEditablePlan(response.data);
      fetchPendingPlans();
    } catch (err) {
      setAssignmentError(apiErrorMessage(err, "Could not remove the complaint from the plan."));
    } finally {
      setPlanBusy(false);
    }
  };

  const reviewPlanItem = async (planId, entryId, action) => {
    if (!isAdmin || busyEntryKey || bulkApprovalBusy) return;
    const entryKey = `${planId}:${entryId}`;
    setBusyEntryKey(entryKey);
    setAssignmentError("");
    try {
      await axios.post(
        `${API_BASE_URL}/visit-plan/plans/${planId}/items/${entryId}/${action}`,
        null,
        { withCredentials: true }
      );
      await fetchPendingPlans();
      if (action === "approve") {
        await fetchOpenComplaints();
        await fetchEditablePlans();
      }
    } catch (err) {
      setAssignmentError(apiErrorMessage(err, `Could not ${action} the visit plan.`));
    } finally {
      setBusyEntryKey("");
    }
  };

  const approveSelectedPlans = async (planIds) => {
    const selectedIds = Array.from(new Set(planIds || [])).filter(Boolean);
    if (!isAdmin || bulkApprovalBusy || busyEntryKey || !selectedIds.length) {
      return false;
    }

    setBulkApprovalBusy(true);
    setAssignmentError("");
    const failedPlanIds = [];

    try {
      for (const planId of selectedIds) {
        try {
          await axios.post(
            `${API_BASE_URL}/visit-plan/plans/${planId}/approve`,
            null,
            { withCredentials: true }
          );
        } catch (err) {
          failedPlanIds.push(planId);
          console.error(`Visit plan ${planId} bulk approval failed:`, err);
        }
      }

      await Promise.all([
        fetchPendingPlans(),
        fetchOpenComplaints(),
        fetchEditablePlans(),
      ]);

      if (failedPlanIds.length) {
        const approvedCount = selectedIds.length - failedPlanIds.length;
        setAssignmentError(
          `${approvedCount} plan(s) approved; ${failedPlanIds.length} could not be approved.`
        );
        return false;
      }

      return true;
    } finally {
      setBulkApprovalBusy(false);
    }
  };

  const updateUserAccess = async (userId, enabled) => {
    if (!isAdmin || busyAccessUserId) return;
    setBusyAccessUserId(userId);
    setAssignmentError("");
    try {
      const response = await axios.put(
        `${API_BASE_URL}/admin/visit-plan-access/${userId}`,
        null,
        { params: { enabled }, withCredentials: true }
      );
      setAccessUsers((previous) =>
        previous.map((user) =>
          user.id === userId ? { ...user, ...response.data } : user
        )
      );
    } catch (err) {
      setAssignmentError(apiErrorMessage(err, "Could not update Visit Plan access."));
    } finally {
      setBusyAccessUserId(null);
    }
  };

  const exportFilteredComplaints = () => {
    if (reportLoading) return;

    setReportLoading(true);
    setReportError("");

    try {
      if (!reviewComplaints.length) {
        throw new Error("No complaints to export.");
      }

      const rows = reviewComplaints.map((complaint, index) => {
        const priority = getVisitPriority(complaint, calculateAgingDays);
        const complaintAge = getComplaintAge(complaint, calculateAgingDays);
        const hardwareDeliveryAge = getHardwareDeliveryAge(complaint);
        const hardwareDeliveryDate = getHardwareDeliveryDate(complaint);
        const visitorStation = getAssignedVisitorStation(complaint);
        const plannedEntry = plannedEntriesByComplaint.get(
          normalize(complaint.complaintId).toLowerCase()
        );

        return {
          "S.No": index + 1,
          Date: complaint.date || "-",
          "Complaint Age": complaintAge >= 0 ? complaintAge : "-",
          "HW Delivery Age": hardwareDeliveryAge >= 0 ? hardwareDeliveryAge : "-",
          "HW Delivery Date": hardwareDeliveryDate || "-",
          "Priority Reason": priority.label || "-",
          "Priority Detail": priority.detail || "-",
          Bank: complaint.bankName || "-",
          "Branch Code": complaint.branchCode || "-",
          "Branch Name": complaint.branchName || "-",
          City: complaint.city || "-",
          Status: complaint.complaintStatus || "Open",
          "Courier Status": getCourierStatus(complaint) || "N/A",
          "Visitor Name": getVisitorName(complaint) || "-",
          Station: visitorStation || "-",
          "Plan Date": getScheduleDateForComplaint(complaint) || planningDate || "-",
          "Plan Status": plannedEntry ? plannedEntry.approvalStatus || "Added" : "Not added",
          "Complaint ID": complaint.complaintId || "-",
        };
      });

      const worksheet = XLSX.utils.json_to_sheet(rows);
      worksheet["!cols"] = [
        { wch: 8 },
        { wch: 12 },
        { wch: 14 },
        { wch: 16 },
        { wch: 16 },
        { wch: 24 },
        { wch: 28 },
        { wch: 12 },
        { wch: 14 },
        { wch: 30 },
        { wch: 18 },
        { wch: 14 },
        { wch: 18 },
        { wch: 24 },
        { wch: 18 },
        { wch: 12 },
        { wch: 14 },
        { wch: 22 },
      ];

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Visit Plan Data");
      const buffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      saveAs(blob, `Visit_Plan_Data_${planningDate || getTodayLabel()}.xlsx`);
    } catch (err) {
      console.error("Visit plan data export failed:", err);
      setReportError(err.message || "Could not export the visit plan data.");
    } finally {
      setReportLoading(false);
    }
  };

  return (
    <div className="visit-plan">
      <PageHeader
        loading={loading}
        lastReviewedAt={lastReviewedAt}
        planningDate={planningDate}
        reportLoading={reportLoading}
        onDownloadReport={exportFilteredComplaints}
        onRefresh={fetchOpenComplaints}
      />

      <WorkflowTabs
        activeView={activeWorkflowView}
        pendingCount={pendingPlans.length}
        isAdmin={isAdmin}
        onChange={(view) => {
          setActiveWorkflowView(view);
          if (view === "approvals") fetchPendingPlans();
        }}
      />

      {(error || assignmentError || planLoadError || reportError) && (
        <div className="visit-plan-alert">
          {assignmentError || planLoadError || reportError || error}
        </div>
      )}

      {activeWorkflowView === "reports" ? (
        <VisitPlanReports embedded />
      ) : isAdmin && activeWorkflowView === "approvals" ? (
        <>
          <VisitPlanAccessPanel
            users={accessUsers}
            loading={accessUsersLoading}
            busyUserId={busyAccessUserId}
            onToggle={updateUserAccess}
          />
          <AdminApprovalPanel
            plans={pendingPlans}
            loading={pendingPlansLoading}
            busyEntryKey={busyEntryKey}
            bulkApprovalBusy={bulkApprovalBusy}
            onApproveItem={(planId, entryId) =>
              reviewPlanItem(planId, entryId, "approve")
            }
            onRejectItem={(planId, entryId) =>
              reviewPlanItem(planId, entryId, "reject")
            }
            onApprovePlans={approveSelectedPlans}
          />
        </>
      ) : (
        <>
          <NewInstallationPanel
            visitors={visitors}
            cities={cities}
            defaultDate={planningDate}
            busy={installationBusy}
            onAdd={addInstallationToPlan}
          />

          <CurrentPlanPanel
            plans={editablePlans}
            busy={planBusy}
            onSubmit={submitEditablePlans}
            onRemove={removePlanItem}
          />

          <PlanningControls
            filters={filters}
            filterOptions={filterOptions}
            routeSettings={routeSettings}
            planningDate={planningDate}
            routeLoading={routeLoading}
            routeSuggestions={routeSuggestions}
            complaintsByCity={complaintsByCity}
            filteredCount={filteredComplaints.length}
            sourceTotal={reviewSourceTotal}
            routeError={routeError}
            selectedRouteCity={selectedRouteCity}
            onChange={handleFilterChange}
            onToggleExcludedCity={toggleExcludedCity}
            onToggleAllExcludedCities={toggleAllExcludedCities}
            onPlanningDateChange={setPlanningDate}
            onClear={clearFilters}
            onRouteSettingChange={handleRouteSettingChange}
            onSuggestRoutes={() => fetchRouteSuggestions()}
            onSelectRoute={selectRouteForReview}
            onReviewRoute={reviewSelectedRoute}
          />

          <ComplaintReviewTable
            complaints={visibleComplaints}
            totalCount={reviewComplaints.length}
            sourceTotal={reviewSourceTotal}
            visibleLimit={visibleLimit}
            planningForDate={planningDate}
            loading={loading}
            visitorsLoading={visitorsLoading}
            savingAssignments={savingAssignments}
            approvingComplaints={approvingComplaints}
            plannedEntriesByComplaint={plannedEntriesByComplaint}
            getScheduleDateForComplaint={getScheduleDateForComplaint}
            onScheduleDateChange={handleComplaintScheduleDateChange}
            calculateAgingDays={calculateAgingDays}
            getAssignedVisitorStation={getAssignedVisitorStation}
            getVisitorOptionsForComplaint={getVisitorOptionsForComplaint}
            assignVisitorToComplaint={assignVisitorToComplaint}
            nearestStationLookups={nearestStationLookups}
            preferredStations={preferredStations}
            findNearestStations={findNearestStations}
            selectPreferredStation={selectPreferredStation}
            saveComplaintToPlan={saveComplaintToPlan}
            onShowMore={showMoreComplaints}
            selectedRoute={selectedRoute}
            selectedRouteOrigin={filters.station}
            onClearRoute={() => setSelectedRouteCity("")}
          />
        </>
      )}
    </div>
  );
};

export default VisitPlan;






